import type { ComponentProps, CSSProperties, JSX } from 'react';

import classNames from 'classnames';

import styles from './Skeleton.module.css';

/** A px number, or any CSS length the caller would rather write itself. */
type Dimension = number | string;

/**
 * `height` and `width` are declared rather than inherited from `div`.
 *
 * They used to fall through `...props` onto the element, and a `div` has no
 * `height` or `width` content attribute — so `<Skeleton height={400} />`
 * rendered `<div height="400">`, which CSS ignores. With nothing else setting a
 * size the placeholder collapsed to zero and the loading state was invisible.
 * TypeScript reported this on every call site; nothing typechecked the
 * dashboard, so nobody saw it.
 */
interface SkeletonProps extends Omit<ComponentProps<'div'>, 'height' | 'width'> {
  height?: Dimension;
  width?: Dimension;
}

const toLength = (value: Dimension): string => (typeof value === 'number' ? `${value}px` : value);

function Skeleton({ className, height, width, style, ...props }: SkeletonProps): JSX.Element {
  // Passed as custom properties, the way SlideoutPanel, Progress and
  // IncubatorList pass dynamic values, rather than as style declarations.
  // Each dimension gets its own class so that an unset one is left entirely
  // alone — a caller sizing the skeleton through `className` keeps working.
  const sizing: CSSProperties = { ...style };
  if (height !== undefined) {
    (sizing as Record<string, string>)['--skeleton-height'] = toLength(height);
  }
  if (width !== undefined) {
    (sizing as Record<string, string>)['--skeleton-width'] = toLength(width);
  }

  return (
    <div
      data-slot="skeleton"
      className={classNames(
        styles.Skeleton,
        height !== undefined && styles.HasHeight,
        width !== undefined && styles.HasWidth,
        className,
      )}
      style={sizing}
      {...props}
    />
  );
}

export { Skeleton };
